import React, { useRef } from "react";
import { X, PlusCircle } from 'lucide-react';
import { useTuitionContext } from '../context/TuitionContext';

const TuitionForm = () => {
    const expenseContainerRef = useRef(null);

    const {
        // State
        currentTuition,
        setCurrentTuition,
        studentCount,
        setStudentCount,
        profitPercentage,
        setProfitPercentage,
        expenses,
        updateExpense,
        setShowPanel,
        setShowInstructions,
        
        customExpenses,
        updateCustomExpense,
        removeCustomExpense,
        addCustomExpense,
        newExpenseLabel,
        setNewExpenseLabel,

        // Calculation methods
        calculateTotalExpenses,
        
        // Utility methods
        validateAndCalculate
    } = useTuitionContext();

    const leftColumnCSS = (colColor) => `d-flex flex-column justify-content-center col-6 col-sm-5 col-lg-4 text-white p-2 border-2 border border-light-grey ${colColor} roboto-bold`;
    const rightColumnCSS = "col-6 col-sm-7 col-lg-8 p-1 border-2 border-left border-top border-bottom border-end border-light-grey roboto-bold";

    const leftColumnCSS2 = (colColor) => `d-flex flex-column justify-content-center col-6 col-sm-5 col-lg-4 text-white p-2 border-2 border-bottom border-start border-end border-light-grey ${colColor} roboto-bold`;
    const rightColumnCSS2 = "col-6 col-sm-7 col-lg-8 p-1 border-2 border-left border-bottom border-end border-light-grey roboto-bold";

    const showInstructions = () => {
        setShowInstructions(true);
        setShowPanel(true);
    }

    const addNewExpense = () => {
        if (newExpenseLabel.trim()) {
            addCustomExpense();
            // Delay scroll until after React re-renders with the new item
            setTimeout(() => {
                scrollToBottom();
            }, 0);
        }
    }
    const scrollToBottom = () => {
        if (expenseContainerRef.current) {
            expenseContainerRef.current.scrollTop = expenseContainerRef.current.scrollHeight;
        }
    }

    return (
        <div className="row justify-content-center">
            <div className="col-12 bg-light rounded-large" id="primary-container">
                <img src="/img/sparkle.svg" id='sparkle-top' alt=""/>

                <div className="row justify-content-center">
                    <div className='col-12'>
                        <div className="d-flex align-items-center justify-content-center">
                            <img src='/img/the-tutoring-center.png' alt='The Tutoring Center®' className="logo mt-3 me-3" />
                            <h1 className="text-dark text-teal text-uppercase fw-bold title-text">
                                Target Tuition<br/>
                                Rate Calculator
                            </h1>
                        </div>
                        {/* Main Content */}
                        <div className="main-content">
                            <div className="card border-0" style={{ backgroundColor:"transparent"}}>
                                <div className="card-body pt-4 ps-4 pe-4 pb-0 mb-0">
                                    <div className='row'>
                                        <div className="w-100 col-12 col-md-10 d-flex align-items-center justify-content-between">
                                            <h5 className="text-teal fw-bold text-uppercase">Current Tuition Information</h5>
                                            <button className="btn btn-link btn-sm text-secondary" onClick={showInstructions}>
                                                Instructions
                                            </button>
                                        </div>
                                    </div>
                                    <div className='row mb-0'>
                                        <div className={leftColumnCSS('bg-gold-gradient')}>
                                            <strong>Current Enrollment</strong>
                                        </div>
                                        <div className={rightColumnCSS}>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={studentCount}
                                                onChange={(e) => setStudentCount(parseInt(e.target.value))}
                                                onWheel={(e) => e.target.blur()}
                                                title="Enter current enrollment"
                                                min="0"
                                                step="1"
                                            />
                                        </div>
                                    </div>
                                    <div className='row mb-4'>
                                        <div className={leftColumnCSS2('bg-gold-gradient')}>
                                            <strong>Current Tuition Rate</strong>
                                        </div>
                                        <div className={rightColumnCSS2}>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={currentTuition}
                                                onChange={(e) => setCurrentTuition(e.target.value)}
                                                onWheel={(e) => e.target.blur()}
                                                title="Enter current tuition amount"
                                                min="0"
                                                step="1"
                                            />
                                        </div>
                                    </div>

                                    {/* Expenses */}
                                    <div className="row">
                                        <div className="col-12" style={{position: 'relative'}}>
                                            <img src="/img/sparkle.svg" id='sparkle-overhead'  alt=""/>
                                            <h5 className="text-teal fw-bold text-uppercase">Overhead & Expenses</h5>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body pt-0 pb-0 mb-0 ps-3 pe-3">
                                    <div className='expense-container' ref={expenseContainerRef}>
                                        {expenses.map((expense, idx) => (
                                            <div className="row row-expense" key={expense.id}>
                                                <div className={`${idx === 0 ? leftColumnCSS('bg-teal') : leftColumnCSS2('bg-teal')} expense-label`}>
                                                    {expense.label}
                                                </div>
                                                <div className={idx === 0 ? rightColumnCSS : rightColumnCSS2}>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        value={expense.value}
                                                        onChange={(e) => updateExpense(expense.id, e.target.value)}
                                                        onWheel={(e) => e.target.blur()}
                                                        title={`Enter ${expense.label} amount`}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                        { customExpenses.length > 0 && 
                                            <div className="row row-expense mt-2">
                                                <div className='expense-label p-2'>
                                                    Custom Expenses
                                                </div>
                                            </div>
                                        }
                                        {customExpenses.map((expense, idx) => (
                                            <div className="row row-expense" key={expense.id}>
                                                <div className={`${idx === 0 ? leftColumnCSS('bg-teal') : leftColumnCSS2('bg-teal')} expense-label`}>
                                                    {expense.label}
                                                </div>
                                                <div className={idx === 0 ? rightColumnCSS : rightColumnCSS2}>
                                                    <div className="input-group input-group-sm">
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        value={expense.value}
                                                        onChange={(e) => updateCustomExpense(expense.id, e.target.value)}
                                                        onWheel={(e) => e.target.blur()}
                                                        title={`Enter ${expense.label} amount`}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    <button
                                                        onClick={() => removeCustomExpense(expense.id)}
                                                        className="btn btn-outline-danger"
                                                        type="button">
                                                        <X size={16} />
                                                    </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                            {/* Add Custom Expense */}
                                        <div className="row row-expense mt-1">
                                            <div className={`${leftColumnCSS2('bg-teal-disabled')} expense-label`}>
                                                + Add Item
                                            </div>
                                            <div className={rightColumnCSS2}>
                                                <div className="input-group input-group-sm">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={newExpenseLabel}
                                                        onChange={(e) => setNewExpenseLabel(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && newExpenseLabel.trim()) {
                                                                addNewExpense();
                                                            }
                                                        }}
                                                        placeholder="New Item Label"
                                                        title={`New Item Label`}
                                                    />
                                                    <button
                                                        onClick={addNewExpense}
                                                        className="btn btn-primary"
                                                        disabled={!newExpenseLabel.trim()}
                                                        type="button"
                                                    >
                                                        <PlusCircle size={16} />
                                                    </button>
                                                </div>                            </div>
                        </div>
                    </div>
                </div>
                                <div class="card-body pt-0 ps-4 pe-4 pb-0 mb-0">
                                    <div className="row mt-3 mb-3">
                                        <div className={`${leftColumnCSS('bg-overhead')} border-overhead rounded-medium-left text-center`}>
                                            <span style={{fontSize: '.95rem'}} className='text-uppercase'>Total Overhead</span>
                                        </div>
                                        <div className={`${rightColumnCSS} border-overhead bg-overhead rounded-medium-right pe-3`}>
                                            <div className="input-group">
                                                <span className="input-group-text">$</span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={calculateTotalExpenses()}
                                                    onWheel={(e) => e.target.blur()}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className={`${leftColumnCSS('bg-profit')} border-profit rounded-medium-left text-center`}>
                                            <span style={{fontSize: '.95rem'}} className='text-uppercase'>Desired Profit Margin</span>
                                        </div>
                                        <div className={`${rightColumnCSS} border-profit bg-profit rounded-medium-right pe-3`}>
                                            <select 
                                                className="form-select text-center" 
                                                value={profitPercentage}
                                                onChange={(e) => setProfitPercentage(parseInt(e.target.value))}
                                            >
                                                {Array.from({ length: 12 }, (_, i) => (i + 1) * 5).map(value => (
                                                    <option key={value} value={value}>{value}%
                                                        {value === 50 ? ' (Default)' : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="row mb-4 calculation-row">
                                    <div className="text-center mt-4">
                                        <button
                                            onClick={validateAndCalculate}
                                            disabled={!currentTuition || !studentCount || calculateTotalExpenses() <= 0}
                                            className="btn btn-lg px-5 w-100 bg-gold-gradient text-white font-weight-bold text-uppercase btn-shadow"
                                        >
                                            <strong>Calculate</strong>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <img src="/img/sparkle.svg" id='sparkle-bottom' alt=""/>
            </div>
        </div>
    )
}

export default TuitionForm;